<template>
    <el-card>
        <div slot="header">
            <span style="font-size: 18px; font-weight: bold">产品参数分组数据</span>
        </div>
        <!-- 1 -->
        <div class="product-param-container">
            <!-- 左侧参数分组列表 -->
            <div class="param-group-list">
                <div class="list-title">
                    <span>参数分组</span>
                    <el-button type="primary" size="mini" @click="handleAddGroup">新增参数分组</el-button>
                </div>
                <el-empty v-if="groupList.length === 0 && !groupLoading" description="暂无参数分组" />
                <div v-else class="group-scroll" @scroll="handleGroupScroll">
                    <el-menu :default-active="activeGroup" class="group-menu" @select="handleGroupSelect">
                        <el-menu-item v-for="group in groupList" :key="String(group.id)" :index="String(group.id)" >
                            <div class="group-item">
                                <el-tooltip class="group-name-tooltip" :content="group.name" placement="right" :enterable="false" :open-delay="500" >
                                    <span class="group-name">{{ group.name }}</span>
                                </el-tooltip>
                                <div class="group-actions">
                                    <el-button class="group-action-btn" type="text" size="mini" icon="el-icon-edit" @click.stop="handleEditGroup(group)" />
                                    <el-button class="group-action-btn group-delete-btn" type="text" size="mini" icon="el-icon-delete" @click.stop="handleDeleteGroup(group)" />
                                </div>
                            </div>
                        </el-menu-item>
                    </el-menu>
                    <div class="load-more">
                        <span v-if="groupLoading">加载中...</span>
                        <span v-else-if="hasMoreGroups">上滑加载更多</span>
                        <span v-else-if="groupList.length">已加载全部 {{ groupTotal }} 个</span>
                    </div>
                </div>
            </div>

            <!-- 右侧子项分页表格 -->
            <div class="item-table-wrapper">
                <div class="table-header">
                    <div class="table-title">{{ tableTitle }}</div>
                    <el-button type="primary" size="mini" @click="handleAddItem">增加参数子项</el-button>
                </div>
                <el-empty v-if="!activeGroup" description="请先选择左侧参数分组" />
                <el-empty v-else-if="itemList.length === 0" description="暂无子项数据" />

                <el-table v-else :data="itemList" border stripe style="width: 100%" :fit="true">
                    <el-table-column prop="sortOrder" label="排序" align="center" min-width="70" fixed="left" show-overflow-tooltip />
                    <el-table-column prop="name" label="参数名称" align="center" min-width="100" show-overflow-tooltip />
                    <el-table-column prop="param" label="参数内容" min-width="360" show-overflow-tooltip />
                    <el-table-column label="操作" align="center" min-width="120" fixed="right">
                        <template slot-scope="scope">
                            <el-button type="text" size="small" @click="handleEditItem(scope.row)">编辑</el-button>
                            <el-button type="text" size="small" style="color: #f56c6c" @click="handleDeleteItem(scope.row)">删除</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <el-pagination
                    v-if="activeGroup && itemList.length > 0"
                    class="pagination"
                    background
                    layout="total, sizes, prev, pager, next, jumper"
                    :current-page="currentPage"
                    :page-size="pageSize"
                    :page-sizes="[10, 20, 50, 100]"
                    :total="itemTotal"
                    @current-change="handlePageChange"
                    @size-change="handleSizeChange"
                />
            </div>
        </div>

        <!-- 新增/编辑参数分组弹窗 -->
        <el-dialog
            :title="dialogTitle"
            :visible.sync="dialogVisible"
            width="500px"
            :close-on-click-modal="false"
            @closed="resetGroupForm"
        >
            <el-form ref="form" :model="form" label-width="120px" :rules="formRules">
                <el-form-item label="参数分组名称" prop="name">
                    <el-input v-model="form.name" placeholder="请输入参数分组名称" maxlength="50" show-word-limit clearable />
                </el-form-item>
                <el-form-item label="排序" prop="sortOrder">
                    <el-input-number v-model="form.sortOrder" :min="0" :max="9999" controls-position="right" />
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button @click="dialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确 定</el-button>
            </div>
        </el-dialog>

        <!-- 新增/编辑参数子项弹窗 -->
        <el-dialog
            :title="itemDialogTitle"
            :visible.sync="itemDialogVisible"
            width="600px"
            :close-on-click-modal="false"
            @closed="resetItemForm"
        >
            <el-form ref="itemFormRef" :model="itemForm" label-width="100px" :rules="itemFormRules">
                <el-form-item label="参数名称" prop="name">
                    <el-input v-model="itemForm.name" placeholder="请输入参数名称" maxlength="50" show-word-limit clearable />
                </el-form-item>
                <el-form-item label="参数内容" prop="param">
                    <el-input v-model="itemForm.param" type="textarea" :rows="4" placeholder="请输入参数内容" />
                </el-form-item>
                <el-form-item label="排序" prop="sortOrder">
                    <el-input-number v-model="itemForm.sortOrder" :min="0" :max="9999" controls-position="right" />
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button @click="itemDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="itemSubmitLoading" @click="handleItemSubmit">确 定</el-button>
            </div>
        </el-dialog>
    </el-card>
</template>

<script>
import {
    productParamGroupList, productParamGroupUpdate, productParamGroupDelete, productParamGroupAdd,
    productParamItemList, productParamItemAdd, productParamItemDelete, productParamItemUpdate, productParamItemDetail
} from '@/api/quote'

export default {
    name: 'ProductParamGroup',
    data() {
        return {
            activeGroup: '',
            currentPage: 1,
            pageSize: 20,
            dialogVisible: false,
            dialogTitle: '新增参数分组',
            isEdit: false,
            submitLoading: false,
            form: {
                id: null,
                name: '',
                sortOrder: 0
            },
            formRules: {
                name: [{ required: true, message: '请输入参数分组名称', trigger: 'blur' }]
            },
            itemDialogVisible: false,
            itemDialogTitle: '新增参数子项',
            itemIsEdit: false,
            itemSubmitLoading: false,
            itemForm: {
                id: null,
                name: '',
                param: '',
                sortOrder: 0
            },
            itemFormRules: {
                name: [{ required: true, message: '请输入参数名称', trigger: 'blur' }],
                param: [{ required: true, message: '请输入参数内容', trigger: 'blur' }]
            },
            groupList: [],
            groupPage: 1,
            groupPageSize: 20,
            groupTotal: 0,
            groupLoading: false,
            itemList: [],
            itemTotal: 0
        }
    },
    computed: {
        tableTitle() {
            if (!this.activeGroup) return '参数子项'
            const group = this.groupList.find(item => String(item.id) === this.activeGroup)
            return group ? `${group.name} - 参数子项` : '参数子项'
        },
        hasMoreGroups() {
            return this.groupList.length < this.groupTotal
        }
    },
    mounted() {
        this.getProductParamGroupList()
    },
    methods: {
        // 获取产品参数分组列表（左侧菜单，滚动分页，append 时追加下一页）
        getProductParamGroupList(append = false) {
            if (this.groupLoading) return
            this.groupLoading = true
            productParamGroupList({
                filter: [],
                sorted: [{ name: 'sortOrder', type: 'ASC' }],
                current: append ? this.groupPage : 1,
                size: this.groupPageSize
            }).then(res => {
                if (res && res.success && res.result) {
                    const records = res.result.records || []
                    this.groupList = append ? this.groupList.concat(records) : records
                    this.groupTotal = res.result.total || 0
                }
            }).catch(error => {
                console.error('获取产品参数分组列表失败：', error)
                if (!append) {
                    this.groupList = []
                    this.groupTotal = 0
                }
            }).finally(() => {
                this.groupLoading = false
            })
        },
        // 左侧列表滚动到底部时加载下一页
        handleGroupScroll(event) {
            const el = event.target
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                this.loadMoreGroups()
            }
        },
        // 加载下一页（追加到列表尾部）
        loadMoreGroups() {
            if (this.groupLoading || !this.hasMoreGroups) return
            this.groupPage += 1
            this.getProductParamGroupList(true)
        },
        // 处理参数分组选择
        handleGroupSelect(index) {
            this.activeGroup = index
            this.currentPage = 1
            this.itemList = []
            this.itemTotal = 0
            this.getProductParamItemList()
        },
        // 获取参数子项列表
        getProductParamItemList() {
            if (!this.activeGroup) return
            productParamItemList({
                filter: [
                    { name: 'groupId', value: Number(this.activeGroup), type: 'EQ' }
                ],
                sorted: [{ name: 'sortOrder', type: 'ASC' }],
                current: this.currentPage,
                size: this.pageSize
            }).then(res => {
                if (res.success && res.result) {
                    this.itemList = res.result.records || []
                    this.itemTotal = res.result.total || 0
                }
            })
        },
        // 处理分页变化
        handlePageChange(page) {
            if (this.currentPage !== page) {
                this.currentPage = page
                this.getProductParamItemList()
            }
        },
        // 处理每页条数变化
        handleSizeChange(size) {
            this.pageSize = size
            this.currentPage = 1
            this.getProductParamItemList()
        },
        // 重置分组表单
        resetGroupForm() {
            this.form = {
                id: null,
                name: '',
                sortOrder: 0
            }
            this.$refs.form && this.$refs.form.clearValidate()
        },
        // 新增参数分组（打开弹窗）
        handleAddGroup() {
            this.isEdit = false
            this.dialogTitle = '新增参数分组'
            this.resetGroupForm()
            this.dialogVisible = true
        },
        // 编辑参数分组（打开弹窗）
        handleEditGroup(group) {
            this.isEdit = true
            this.dialogTitle = '编辑参数分组'
            this.form = {
                id: group.id,
                name: group.name,
                sortOrder: group.sortOrder
            }
            this.dialogVisible = true
            this.$nextTick(() => {
                if (this.$refs.form) this.$refs.form.clearValidate()
            })
        },
        // 保存参数分组
        handleSubmit() {
            this.$refs.form.validate(async valid => {
                if (!valid) return
                this.submitLoading = true
                const groupData = {
                    name: this.form.name,
                    sortOrder: this.form.sortOrder
                }
                try {
                    let res
                    if (this.isEdit) {
                        groupData.id = this.form.id
                        res = await productParamGroupUpdate(groupData)
                    } else {
                        res = await productParamGroupAdd(groupData)
                    }
                    if (res && res.code === '0000') {
                        this.$message.success(this.isEdit ? '修改成功' : '添加成功')
                        this.dialogVisible = false
                        this.groupPage = 1
                        this.getProductParamGroupList()
                    } else {
                        this.$message.error(res.message || (this.isEdit ? '修改失败' : '添加失败'))
                    }
                } catch (error) {
                    console.error('保存参数分组失败：', error)
                    this.$message.error(this.isEdit ? '修改失败' : '添加失败')
                } finally {
                    this.submitLoading = false
                }
            })
        },
        // 删除参数分组
        handleDeleteGroup(group) {
            this.$confirm(`确认删除参数分组「${group.name}」?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await productParamGroupDelete(group.id)
                    if (res && res.code === '0000') {
                        this.$message.success('删除成功')
                        if (this.activeGroup === String(group.id)) {
                            this.activeGroup = ''
                            this.itemList = []
                            this.itemTotal = 0
                        }
                        this.groupPage = 1
                        this.getProductParamGroupList()
                    } else {
                        this.$message.error(res.message || '删除失败')
                    }
                } catch (error) {
                    console.error('删除参数分组失败：', error)
                    this.$message.error('删除失败')
                }
            }).catch(() => {})
        },
        // 重置子项表单
        resetItemForm() {
            this.itemForm = {
                id: null,
                name: '',
                param: '',
                sortOrder: 0
            }
            this.$refs.itemFormRef && this.$refs.itemFormRef.clearValidate()
        },
        // 打开新增子项弹窗
        handleAddItem() {
            if (!this.activeGroup) {
                this.$message.warning('请先选择左侧参数分组')
                return
            }
            this.itemIsEdit = false
            this.itemDialogTitle = '新增参数子项'
            this.resetItemForm()
            this.itemDialogVisible = true
        },
        // 打开编辑子项弹窗
        async handleEditItem(row) {
            this.itemIsEdit = true
            this.itemDialogTitle = '编辑参数子项'
            this.resetItemForm()
            this.itemDialogVisible = true
            try {
                const res = await productParamItemDetail(row.id)
                if (res && res.code === '0000') {
                    const data = res.result || {}
                    this.itemForm = {
                        id: data.id,
                        name: data.name,
                        param: data.param,
                        sortOrder: data.sortOrder
                    }
                } else {
                    this.$message.error(res.message || '获取子项详情失败')
                    this.itemDialogVisible = false
                }
            } catch (error) {
                console.error('获取参数子项详情失败：', error)
                this.$message.error('获取子项详情失败')
                this.itemDialogVisible = false
            }
        },
        // 保存子项
        handleItemSubmit() {
            this.$refs.itemFormRef.validate(async valid => {
                if (!valid) return
                this.itemSubmitLoading = true
                try {
                    const paramData = {
                        groupId: Number(this.activeGroup),
                        name: this.itemForm.name,
                        param: this.itemForm.param,
                        sortOrder: this.itemForm.sortOrder
                    }

                    let res
                    if (this.itemIsEdit) {
                        paramData.id = this.itemForm.id
                        res = await productParamItemUpdate(paramData)
                    } else {
                        res = await productParamItemAdd(paramData)
                    }

                    if (res && res.code === '0000') {
                        this.$message.success(this.itemIsEdit ? '修改成功' : '添加成功')
                        this.itemDialogVisible = false
                        this.getProductParamItemList()
                    } else {
                        this.$message.error(res.message || (this.itemIsEdit ? '修改失败' : '添加失败'))
                    }
                } catch (error) {
                    console.error('保存参数子项失败：', error)
                    this.$message.error('保存失败')
                } finally {
                    this.itemSubmitLoading = false
                }
            })
        },
        // 删除子项
        handleDeleteItem(row) {
            this.$confirm('确认删除该参数子项?', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await productParamItemDelete(row.id)
                    if (res && res.code === '0000') {
                        this.$message.success('删除成功')
                        if (this.itemList.length === 1 && this.currentPage > 1) {
                            this.currentPage -= 1
                        }
                        this.getProductParamItemList()
                    } else {
                        this.$message.error(res.message || '删除失败')
                    }
                } catch (error) {
                    console.error('删除参数子项失败：', error)
                    this.$message.error('删除失败')
                }
            }).catch(() => {})
        }
    }
}
</script>

<style scoped>
.product-param-container {
    display: flex;
    gap: 20px;
    min-height: 500px;
    flex-wrap: nowrap;
}

.param-group-list {
    width: 280px;
    min-width: 260px;
    flex-shrink: 0;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

/* 左侧参数分组列表滚动容器：滚动到底部时加载下一页 */
.group-scroll {
    flex: 1;
    max-height: 480px;
    overflow-y: auto;
    overflow-x: hidden;
}

.load-more {
    padding: 10px 0;
    text-align: center;
    font-size: 12px;
    color: #909399;
}

.list-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    font-size: 16px;
    font-weight: bold;
    color: #303133;
    background-color: #f5f7fa;
    border-bottom: 1px solid #ebeef5;
}

.group-menu {
    border-right: none;
}

.group-menu .el-menu-item {
    height: auto;
    line-height: 1.5;
    padding: 5px 20px !important;
    min-width: 0;
    overflow: hidden;
}

.group-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    width: 100%;
    min-width: 0;
}

.group-name-tooltip {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.group-name {
    display: block;
    min-width: 0;
    font-size: 14px;
    color: #606266;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.group-actions {
    display: none;
    flex-shrink: 0;
    align-items: center;
    gap: 2px;
}

.group-item:hover .group-actions {
    display: flex;
}

.group-action-btn {
    padding: 0;
    color: #409eff;
}

.group-delete-btn {
    color: #f56c6c;
}

.item-table-wrapper {
    flex: 1;
    min-width: 0;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 20px;
    background-color: #fff;
    overflow-x: auto;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebeef5;
}

.table-title {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
}

.pagination {
    margin-top: 20px;
    text-align: right;
}
</style>